import { AnalyticsService } from '@alison/analytics/client';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { formatDuration } from '../../../lib/dateUtils.js';
import type { DetailCreative } from '../data/api.js';
import { NetworkGlyph } from '../NetworkGlyph.js';
import { ELLIPSIS_SX } from '../utils.js';
import { DETAIL_GUTTER } from './shared/SectionHeader.js';

const COPIED_ICON_MS = 1500;

const PAGER_BUTTON_SX = (theme: Theme) => ({
  width: 28,
  height: 28,
  border: '1px solid',
  borderColor: theme.palette.border.strong,
  color: 'text.secondary',
});

interface CreativeDetailHeaderProps {
  creative: DetailCreative;
  position: number;
  total: number;
  contextLabel?: string;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function CreativeDetailHeader({
  creative,
  position,
  total,
  contextLabel,
  onPrevious,
  onNext,
  onClose,
}: CreativeDetailHeaderProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const meta = [
    sentenceCase(creative.creativeType ?? 'Creative'),
    creative.activeDays === null ? null : `${creative.activeDays}d live`,
    creative.format === null ? null : sentenceCase(creative.format),
    formatDuration(creative.duration),
  ].filter((part) => part !== null && part !== '');

  function handleCopy(): void {
    if (!navigator.clipboard) return;
    AnalyticsService.track('Creative Detail - Copy Preview');
    navigator.clipboard.writeText(creative.creativeId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_ICON_MS);
    });
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, px: DETAIL_GUTTER, pt: 4 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {contextLabel !== undefined && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            {contextLabel}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Tooltip title={creative.creativeId} placement="bottom-start">
            <Typography sx={{ fontSize: 15, fontWeight: 600, minWidth: 0, ...ELLIPSIS_SX }}>
              {creative.creativeId}
            </Typography>
          </Tooltip>
          <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top">
            <IconButton size="small" aria-label="Copy" onClick={handleCopy}>
              <ContentCopyOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <NetworkGlyph network={creative.network} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{meta.join(' · ')}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <IconButton
          aria-label="Previous creative"
          disabled={position <= 1}
          onClick={onPrevious}
          sx={PAGER_BUTTON_SX}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          sx={{ fontSize: 12, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        >
          {position} / {total}
        </Typography>
        <IconButton
          aria-label="Next creative"
          disabled={position >= total}
          onClick={onNext}
          sx={PAGER_BUTTON_SX}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton aria-label="Close" onClick={onClose} sx={{ ml: 2, color: 'text.secondary' }}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
